import { Fragment, FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	faChevronRight,
	faGear,
	faSort,
	faSortDown,
	faSortUp,
} from "@fortawesome/pro-regular-svg-icons";

import { Order, SortParams } from "@/types/types";
import { classNames } from "@/utils/classNames";

import { Icon } from "../Icon/Icon";

import "./table.css";
import { AddColumnBodyCell, AddColumnHeaderCell, TableFoot } from "./TableAddControls";
import { useInfiniteScroll } from "./useInfiniteScroll";
import { useStickyMaxHeight } from "./useStickyMaxHeight";
import { TableFooter } from "../TableFooter/TableFooter";
import { Spinner } from "../Spinner/Spinner";
import { TItem } from "../SortableList/SortableList";
import { TableEmptyRow } from "./TableEmptyRow";
import { renderTableSkeleton } from "./tableSkeleton";

import { TableProps } from "./Table.types";
import {
	cycleColumnSort,
	seedDefaultExpandedRows,
	stickyColumnWidth,
	toggleExpandedRow,
} from "./table.helpers";
import { useTableStickyMeasurements } from "./useTableStickyMeasurements";

export type { TableColumn, TableProps } from "./Table.types";

export const Table: FunctionComponent<TableProps> = ({
	data,
	overallCount,
	isLoading = false,
	isFetching = false,
	columns,
	handleSorting,
	handleFetchMore,
	page,
	noDataText,
	noDataCta,
	testId,
	hideFooter,
	showFooterFunctions,
	handleExport,
	handleArchive,
	handleDelete,
	currentlyDownloadingSelected,
	hideColumnsCount,
	totalPages = 1,
	handleFetchPage,
	handleChangeRowsPerPage,
	limit = 15,
	hidePagination,
	handleRowClick,
	handleRowClickWithStringId,
	lazyLoadPagination,
	exportIsLoading,
	sticky = false,
	tableConfig,
	addToProfileTracker,
	disqualify,
	parked,
	unpark,
	archive,
	addToBlacklist,
	removeFromBlacklist,
	unflagCompetitor,
	addToList,
	searchForEmail,
	addToWebhook,
	addToAnotherList,
	addToCrm,
	deleteOrganizations,
	extraActions,
	snooze,
	unsnooze,
	unarchive,
	unarchiveToReview,
	searchForPhone,
	searchForCompanyEmailAndPhone,
	leadScoring,
	renderExpandedRow,
	getSubRows,
	canExpand,
	defaultExpanded = false,
	onExpandRow,
	selectedCount,
	clearSelection,
	deepResearch,
	bulkFindPerson,
	reachOut,
	enrichAnyway,
	footerRow,
	addColumn,
	addRow,
}) => {
	const { t } = useTranslation();

	const [sortedColumn, setSortedColumn] = useState<null | number>(0);
	const [expandedRows, setExpandedRows] = useState<Set<number | string>>(new Set());
	const [columnData, setColumnData] = useState(columns);
	const [currentPage, setCurrentPage] = useState(page || 1);

	useEffect(() => {
		if (page && !lazyLoadPagination) {
			setCurrentPage(page);
		}
	}, [page, lazyLoadPagination]);

	useEffect(() => {
		if (JSON.stringify(columns) !== JSON.stringify(columnData)) {
			setColumnData(columns);
		}
	}, [columns]);

	const setPage = (page: number) => {
		if (handleFetchPage) {
			handleFetchPage(page);
		}

		setCurrentPage(page);
	};

	const startIndex = (currentPage - 1) * limit;

	useInfiniteScroll({ handleFetchMore, isFetching, currentPage, totalPages, page, sticky });

	const handleSortColumn = (index: number, category: string) => {
		const { newColumnData, orderStatus } = cycleColumnSort(columnData, sortedColumn, index);

		setColumnData(newColumnData);

		if (orderStatus === 0) {
			// Third click: remove sorting
			handleSorting?.({ sortBy: "", sortOrder: Order.ASC });
			setSortedColumn(null);

			return;
		}

		handleSorting?.({ sortBy: category, sortOrder: orderStatus === 2 ? Order.DESC : Order.ASC });
		setSortedColumn(index);
	};

	const outerRef = useRef<HTMLDivElement>(null);
	const stickyMaxHeight = useStickyMaxHeight(outerRef, sticky);
	const { scrollContainerRef, thRefs, measuredWidths, viewportWidth } =
		useTableStickyMeasurements();

	// Rows already auto-expanded once (defaultExpanded) — a row the user collapsed stays collapsed
	// when later pages append to `data` and this effect re-runs.
	const seededExpandedIds = useRef<Set<number | string>>(new Set());

	useEffect(() => {
		if (!defaultExpanded) {
			return;
		}

		const seeded = seedDefaultExpandedRows(seededExpandedIds.current, data);

		if (seeded) {
			setExpandedRows((prev) => {
				const next = new Set(prev);

				seeded.forEach((id) => next.add(id));

				return next;
			});
		}
	}, [defaultExpanded, data]);

	const getStickyWidth = (col: { uid?: string; calcWidth?: number }) =>
		stickyColumnWidth(col, measuredWidths);

	const toggleExpanded = (id: number | string) => {
		setExpandedRows((prev) => toggleExpandedRow(prev, id));
	};

	let currentLeftHeaderWidth = 0;
	let currentLeftWidth = 0;

	return (
		<div
			ref={outerRef}
			className={classNames("w-full", sticky ? "flex flex-col" : "")}
			style={sticky ? { height: stickyMaxHeight } : undefined}
		>
			<div
				ref={scrollContainerRef}
				className={classNames(
					"w-full",
					// Selection clearance (last row must stay scrollable above the floating bulk bar):
					// container padding would lift the sticky-bottom add-row tfoot off the viewport edge
					// (sticky insets resolve against the content box), and the 44px tfoot already provides
					// the clearance itself — so skip the padding when an addRow is present.
					(sticky ? selectedCount && selectedCount > 0 && !addRow : !hidePagination)
						? "pb-[38px]"
						: "",
					sticky ? "overflow-auto flex-1 min-h-0" : "overflow-y-auto"
				)}
				id="table-block"
			>
				<table
					className={classNames(
						"group/table w-full text-left rounded-lg border-separate border-spacing-0 border-white",
						// When showing the empty state, fill the scroll viewport's height so the empty
						// cell can be vertically centered in view (a sticky table stretches to the
						// viewport height, otherwise the fixed-height empty cell sits pinned at the top).
						sticky && !isLoading && !data.length ? "h-full" : ""
					)}
					data-test={testId}
				>
					<colgroup>
						{columns
							.filter((c) => c.show || c.show === undefined)
							.map(({ width, minWidth, maxWidth, moveable, show, locked }, index, visibleCols) => {
								const isSticky = (!moveable || locked) && show;

								if (isSticky) {
									currentLeftHeaderWidth = visibleCols
										.filter((c, i) => i < index && (!c.moveable || c.locked) && c.show)
										.reduce((acc, c) => acc + getStickyWidth(c), 0);
								}

								return (
									<col
										key={"col-" + index}
										className={classNames(
											"border-white",
											width === "fit-content" ? "whitespace-nowrap" : "",
											minWidth ? `min-w-[${minWidth}px]` : "",
											maxWidth ? `max-w-[${maxWidth}px]` : "",
											isSticky ? `sticky bg-white group-hover:bg-brand-white` : ""
										)}
										style={{
											width: width || "auto",
											minWidth: minWidth || "auto",
											maxWidth: maxWidth || "auto",
											left: currentLeftHeaderWidth || "auto",
										}}
									/>
								);
							})}
						{addColumn && <col style={{ width: 52 }} />}
					</colgroup>
					<thead
						className={classNames(sticky ? "sticky top-0 bg-brand-white" : "")}
						style={sticky ? { zIndex: 120 } : undefined}
					>
						<tr className="">
							{columnData
								.filter((c) => c.show || c.show === undefined)
								.map((column, index, visibleCols) => {
									const {
										align = "left",
										orderTitle,
										orderStatus,
										onHeaderClick,
										moveable,
										show,
										locked,
									} = column;

									const isSticky = (!moveable || locked) && show;

									if (isSticky) {
										currentLeftHeaderWidth = visibleCols
											.filter((c, i) => i < index && (!c.moveable || c.locked) && c.show)
											.reduce((acc, c) => acc + getStickyWidth(c), 0);
									}

									return (
										<th
											key={`th-${index}`}
											data-uid={column.uid}
											ref={(el) => {
												if (el && column.uid) {
													thRefs.current.set(column.uid, el);
												}
											}}
											className={classNames(
												"px-3 py-1.5 text-xs font-semibold uppercase text-text-meta border-b border-border-light",
												lazyLoadPagination || sticky ? "sticky top-0 bg-brand-white" : "",
												sticky ? "bg-brand-white" : "",
												align === "left" ? "text-left" : "text-right",
												columns[index]?.width === "fit-content"
													? "whitespace-nowrap"
													: columns[index]?.width
														? `!w-[${columns[index]?.width}]`
														: "",
												columns[index]?.minWidth ? `min-w-[${column.minWidth}]` : "",
												columns[index]?.maxWidth ? `max-w-[${column.maxWidth}]` : "",
												isSticky ? `sticky bg-brand-white` : ""
											)}
											style={{
												left: isSticky ? currentLeftHeaderWidth : "auto",
												zIndex: isSticky ? 130 : lazyLoadPagination || sticky ? 120 : undefined,
												...(!isSticky ? { backgroundColor: "#F9FAFB" } : {}),
											}}
										>
											{onHeaderClick ? (
												<div
													className="flex items-center cursor-pointer flex-row "
													onClick={() => onHeaderClick(column.title)}
												>
													<div className="flex-grow flex-col flex ">
														{column.icon && <Icon icon={column.icon} />}{" "}
														{column?.iconRender ? column?.iconRender() : ""}
														{column.svgIcon && (
															<img
																className="text-gray-500 w-[16px] h-[16px] mr-2"
																src={column.svgIcon}
															/>
														)}
														{column.title}
													</div>
													<Icon
														className={classNames(
															"ml-1 w-[10px] h-[10px]",
															orderStatus === 1 || orderStatus === 2
																? "text-gray-700"
																: "text-gray-500"
														)}
														icon={
															orderStatus === 1 ? faSortUp : orderStatus === 2 ? faSortDown : faSort
														}
													/>
												</div>
											) : orderTitle ? (
												<div
													className="flex items-center cursor-pointer"
													onClick={() =>
														handleSortColumn(
															columns.map((el) => el.orderTitle).indexOf(orderTitle),
															orderTitle
														)
													}
												>
													<div className="flex-grow flex-col flex">
														<span
															className={classNames(
																"flex-grow flex items-center whitespace-nowrap",
																[1, 2].includes(orderStatus || 0)
																	? "font-bold text-blue-action"
																	: "font-normal text-table-header"
															)}
														>
															{column.title}{" "}
															{column.icon && (
																<Icon className="ml-2" color="#9CA3AF" icon={column.icon} />
															)}
															{column?.iconRender ? column?.iconRender() : ""}
															{column.svgIcon && (
																<img className="ml-2 h-[18px] text-gray-500" src={column.svgIcon} />
															)}
															{!!column.setEditableColumn && !!column.setShowEditColumn && (
																<Icon
																	className="ml-2 mr-8 h-[18px]  text-gray-500 hover:text-gray-700"
																	icon={faGear}
																	onClick={(e: MouseEvent) => {
																		e.stopPropagation();

																		const columnAsTItem: TItem = tableConfig?.find(
																			(c) => c.uid === column.uid
																		) as TItem;

																		column?.setEditableColumn?.(columnAsTItem);
																		column?.setShowEditColumn?.(true);
																	}}
																/>
															)}
														</span>
													</div>
													<Icon
														className={classNames(
															"ml-1 w-[10px] h-[10px]",
															orderStatus === 1 || orderStatus === 2
																? "text-gray-700"
																: "text-gray-500"
														)}
														icon={
															orderStatus === 1 ? faSortUp : orderStatus === 2 ? faSortDown : faSort
														}
													/>
												</div>
											) : (
												column.title
											)}
										</th>
									);
								})}
							{addColumn && (
								<AddColumnHeaderCell
									addColumn={addColumn}
									stickyTop={!!(lazyLoadPagination || sticky)}
								/>
							)}
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							renderTableSkeleton(columns)
						) : !data.length ? (
							<TableEmptyRow
								colSpan={columns.length + (addColumn ? 1 : 0)}
								noDataCta={noDataCta}
								noDataText={noDataText}
								viewportWidth={viewportWidth}
							/>
						) : (
							data.map((item, rowIndex) => {
								const displayIndex = startIndex + rowIndex;
								const subRows = getSubRows?.(item);
								const isExpanded = expandedRows.has(item.id);
								// Chevron shows from `canExpand` (a cheap signal) when provided, otherwise from
								// whether sub-rows are already loaded.
								const canShowChevron = canExpand ? canExpand(item) : !!subRows?.length;
								const hasSubRows = !!subRows?.length;
								const visibleColumns = columns.filter((c) => c.show || c.show === undefined);

								return (
									<Fragment key={`row-${rowIndex}`}>
										<tr
											className={classNames(
												"bg-white hover:bg-brand-white border-b border-border-light group",
												handleRowClick || handleRowClickWithStringId || renderExpandedRow
													? "cursor-pointer"
													: ""
											)}
											onClick={(e: React.MouseEvent<HTMLTableRowElement>) => {
												// `getSubRows` rows expand only via their dedicated chevron toggle, never on a
												// full-row click (the row carries its own links/hover actions).
												if (renderExpandedRow && !getSubRows) {
													toggleExpanded(item.id);
												}

												if (handleRowClick) {
													handleRowClick(item.id, e);
												} else if (handleRowClickWithStringId) {
													handleRowClickWithStringId(item.id, e);
												}
											}}
										>
											{visibleColumns.map((column, index) => {
												const { render } = column;
												const content = column.isExpandToggle ? (
													canShowChevron ? (
														<button
															aria-expanded={isExpanded}
															aria-label={isExpanded ? t("basics.collapse") : t("basics.expand")}
															className="flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
															type="button"
															onClick={(e) => {
																e.stopPropagation();

																if (!isExpanded) {
																	onExpandRow?.(item);
																}

																toggleExpanded(item.id);
															}}
														>
															<Icon
																className={classNames(
																	"w-4 h-4 transition-transform",
																	isExpanded ? "rotate-90" : ""
																)}
																icon={faChevronRight}
															/>
														</button>
													) : (
														<span className="block h-[22px] w-[22px]" />
													)
												) : typeof render === "string" ? (
													<span
														key={`column-${rowIndex}-${index}`}
														className={classNames("text-sm leading-6 truncate ...")}
													>
														{item[render] || "-"}
													</span>
												) : (
													render(item, displayIndex)
												);
												const { align = "left", moveable, show, locked } = column;

												const isSticky = (!moveable || locked) && show;

												const visibleCols = columns.filter((c) => c.show || c.show === undefined);

												if (isSticky) {
													currentLeftWidth = visibleCols
														.filter((c, i) => i < index && (!c.moveable || c.locked) && c.show)
														.reduce((acc, c) => acc + getStickyWidth(c), 0);
												}

												const currentColumnIsTheLastStickyColumn =
													visibleCols.map((c) => !c.moveable || c.locked).lastIndexOf(true) ===
													index;

												return (
													<td
														key={`item-${rowIndex}-${index}`}
														className={classNames(
															" px-3 py-1.5 text-sm font-normal leading-6 border-b border-border-light",
															align === "left" ? "text-left" : "text-right",
															column.width === "fit-content" ? "whitespace-nowrap" : "",
															column.minWidth ? `min-w-[${column.minWidth}]` : "",
															column.maxWidth ? `max-w-[${column.maxWidth}]` : "",
															isSticky ? `sticky bg-white group-hover:bg-brand-white` : "",
															currentColumnIsTheLastStickyColumn ? "border-r !border-gray-200" : ""
														)}
														style={{
															left: isSticky ? currentLeftWidth : "auto",
															...(isSticky ? { zIndex: 110 } : {}),
														}}
													>
														{content}
													</td>
												);
											})}
											{addColumn && <AddColumnBodyCell onAdd={addColumn.onAdd} />}
										</tr>
										{isExpanded &&
											hasSubRows &&
											subRows.map((sub, subIndex) => (
												<tr
													key={`subrow-${rowIndex}-${subIndex}`}
													className="group/subrow border-b border-border-light"
												>
													{visibleColumns.map((column, index) => (
														<td
															key={`sub-${rowIndex}-${subIndex}-${index}`}
															className={classNames(
																"bg-background-light px-3 py-1.5 text-sm font-normal leading-6 border-b border-border-light",
																column.align === "right" ? "text-right" : "text-left"
															)}
														>
															{column.renderSubRow ? column.renderSubRow(sub, item) : null}
														</td>
													))}
													{addColumn && (
														<td className="border-b border-border-light bg-background-light" />
													)}
												</tr>
											))}
										{renderExpandedRow && expandedRows.has(item.id) && (
											<tr>
												<td
													className="p-0"
													colSpan={
														columns.filter((c) => c.show || c.show === undefined).length +
														(addColumn ? 1 : 0)
													}
												>
													{renderExpandedRow(item)}
												</td>
											</tr>
										)}
									</Fragment>
								);
							})
						)}
					</tbody>
					{(footerRow || addRow) && (
						<TableFoot
							addRow={addRow}
							columns={columns}
							footerRow={footerRow}
							hasAddColumn={!!addColumn}
						/>
					)}
				</table>
			</div>
			<div className={sticky ? "flex-shrink-0" : ""}>
				{isFetching && !isLoading && (
					<div className="flex items-center justify-center">
						<Spinner color="#6B7280" />
					</div>
				)}
			</div>
			<TableFooter
				addToBlacklist={addToBlacklist}
				addToList={addToList}
				addToProfileTracker={addToProfileTracker}
				addToWebhook={addToWebhook}
				addToAnotherList={addToAnotherList}
				addToCrm={addToCrm}
				archive={archive}
				clearSelection={clearSelection}
				columns={columns.length}
				coulumsShown={columns.filter((c) => c.show || c.show === undefined).length}
				currentPage={currentPage}
				currentlyDownloadingSelected={currentlyDownloadingSelected}
				deleteOrganizations={deleteOrganizations}
				extraActions={extraActions}
				disqualify={disqualify}
				exportIsLoading={exportIsLoading}
				handleArchive={handleArchive}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleDelete={handleDelete}
				handleExport={handleExport}
				handleNavigateToPage={setPage}
				hideColumnsCount={hideColumnsCount}
				hideFooter={hideFooter}
				hidePagination={hidePagination}
				leadScoring={leadScoring}
				deepResearch={deepResearch}
				bulkFindPerson={bulkFindPerson}
				reachOut={reachOut}
				enrichAnyway={enrichAnyway}
				limit={limit}
				overallCount={overallCount ?? undefined}
				parked={parked}
				removeFromBlacklist={removeFromBlacklist}
				unflagCompetitor={unflagCompetitor}
				rowsPerPage={data.length}
				searchForCompanyEmailAndPhone={searchForCompanyEmailAndPhone}
				searchForEmail={searchForEmail}
				searchForPhone={searchForPhone}
				selectedCount={selectedCount}
				showFooterFunctions={showFooterFunctions}
				snooze={snooze}
				totalPages={totalPages}
				unarchive={unarchive}
				unarchiveToReview={unarchiveToReview}
				unpark={unpark}
				unsnooze={unsnooze}
			/>
		</div>
	);
};
